import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Eye, MessageSquareText, Star, TrendingDown, TrendingUp, Users, type LucideIcon } from 'lucide-react';
import { Chargement, MessageErreur } from '../components/ui/Primitives';
import { ChampFiltre } from '../components/ui/Onglets';
import { ChampDate } from '../components/ui/ChampDate';
import { ErreurApi } from '../lib/api';
import type { ChoixPeriode } from '../lib/useDashboardDirection';
import { texteNps, type CommentaireSatisfaction, type CritereSatisfaction } from '../lib/useQuestionnaires';
import {
  useStatistiquesFormateur,
  type FiltreStatistiques,
  type ModuleVu,
  type PointVues,
  type Statistiques,
  type StatistiquesFormateur,
} from '../lib/useEspaceFormateur';

/**
 * « Statistiques » (écran `instructor-stats`, TF-459 ; maquette
 * `docs/maquette/modules/06-espaces-apprenant-formateur-direction.js`).
 *
 * Sur la période choisie : les heures animées — celles des fiches séquence
 * validées, face à la période précédente —, les apprenants de ses promotions,
 * les vues des leçons de ses cours et les modules les plus vus, les retours de
 * ses apprenants référents aux questionnaires de satisfaction. La direction
 * choisit un formateur de ses antennes. Tout vient de l'API : l'écran ne
 * recompte rien.
 */

const SANS_FILTRE: FiltreStatistiques = { periode: 'MOIS', du: '', au: '', formateur: '' };

const FORMAT_HEURES = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
const FORMAT_NOMBRE = new Intl.NumberFormat('fr-FR');

function heures(valeur: number): string {
  return `${FORMAT_HEURES.format(valeur)} h`;
}

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${FORMAT_NOMBRE.format(nombre)} ${nombre > 1 ? plurielForme : singulier}`;
}

function surCinq(valeur: number | null): string {
  return valeur === null ? '—' : `${valeur.toFixed(1).replace('.', ',')} / 5`;
}

function jourFrancais(jour: string): string {
  return jour.split('-').reverse().join('/');
}

function heureDeMiseAJour(instant: string): string {
  return new Date(instant).toLocaleTimeString('fr-FR', {
    timeZone: 'Indian/Reunion',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Indicateurs
// ---------------------------------------------------------------------------

function Indicateur({
  icone: IconeIndicateur,
  teinte,
  libelle,
  valeur,
  tendance,
  detail,
}: {
  icone: LucideIcon;
  teinte: string;
  libelle: string;
  valeur: string;
  tendance?: JSX.Element | null;
  detail: string;
}): JSX.Element {
  return (
    <div className="stat-card min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${teinte}`}>
        <IconeIndicateur className="h-5 w-5" aria-hidden />
      </div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-codialis-gray sm:text-xs">{libelle}</div>
      <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">{valeur}</div>
      {tendance}
      <div className="mt-1 text-xs text-codialis-gray">{detail}</div>
    </div>
  );
}

/** « +18 % vs période précédente », signé, avec sa flèche ; rien à comparer : dit tel quel. */
function TendanceHeures({ heures: lues }: { heures: Statistiques['heures'] }): JSX.Element {
  if (lues.ecart === null) {
    return <div className="mt-1 text-xs text-codialis-gray">Aucune heure validée sur la période précédente</div>;
  }
  const Fleche = lues.ecart < 0 ? TrendingDown : TrendingUp;
  const signe = lues.ecart > 0 ? '+' : lues.ecart < 0 ? '−' : '±';
  return (
    <div
      className={`mt-1 flex items-center gap-1 text-xs font-medium ${
        lues.ecart < 0 ? 'text-amber-700' : lues.ecart > 0 ? 'text-emerald-700' : 'text-codialis-gray'
      }`}
    >
      <Fleche className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {signe}
      {Math.abs(lues.ecart)} % vs période précédente ({heures(lues.precedentes)})
    </div>
  );
}

function Indicateurs({ statistiques, direction }: { statistiques: Statistiques; direction: boolean }): JSX.Element {
  const { heures: lues, apprenants, vues, satisfaction } = statistiques;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Indicateur
        icone={Clock}
        teinte="bg-amber-100 text-amber-600"
        libelle="Heures animées"
        valeur={heures(lues.validees)}
        tendance={<TendanceHeures heures={lues} />}
        detail={[
          `Fiches séquence validées : ${FORMAT_NOMBRE.format(lues.fichesValidees)}`,
          lues.enAttente > 0 ? `${heures(lues.enAttente)} en attente de validation` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
      <Indicateur
        icone={Users}
        teinte="bg-codialis-sky/10 text-codialis-sky"
        libelle="Apprenants suivis"
        valeur={FORMAT_NOMBRE.format(apprenants.enFormation)}
        detail={`Aujourd’hui · ${pluriel(apprenants.promotions, 'promotion')} · ${FORMAT_NOMBRE.format(apprenants.referent)} en référent`}
      />
      <Indicateur
        icone={Eye}
        teinte="bg-codialis-mint/10 text-codialis-teal"
        libelle={direction ? 'Vues de ses leçons' : 'Vues de vos leçons'}
        valeur={FORMAT_NOMBRE.format(vues.total)}
        detail={vues.total === 0 ? 'Aucune sur la période' : `Par ${pluriel(vues.apprenants, 'apprenant')}`}
      />
      <Indicateur
        icone={Star}
        teinte="bg-codialis-mint/10 text-codialis-teal"
        libelle="Note moyenne"
        valeur={surCinq(satisfaction.moyenne)}
        detail={satisfaction.reponses === 0 ? 'Aucun retour sur la période' : pluriel(satisfaction.reponses, 'retour')}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vues des leçons
// ---------------------------------------------------------------------------

const LIBELLES_PAS = { JOUR: 'Par jour', SEMAINE: 'Par semaine', MOIS: 'Par mois' } as const;

/** Six étiquettes au plus sous l'axe : les autres se lisent au survol et dans le tableau. */
const ETIQUETTES_MAX = 6;

function Histogramme({ points }: { points: PointVues[] }): JSX.Element {
  const [actif, setActif] = useState<number | null>(null);
  const max = Math.max(0, ...points.map((point) => point.vues));
  const intervalle = Math.max(1, Math.ceil(points.length / ETIQUETTES_MAX));
  const point = actif === null ? null : points[actif];
  const position = actif === null ? 0 : ((actif + 0.5) / points.length) * 100;

  return (
    <div>
      <div className="flex h-48 gap-2">
        <div className="flex w-6 shrink-0 flex-col justify-between text-right text-[10px] text-codialis-gray" aria-hidden>
          <span>{FORMAT_NOMBRE.format(max)}</span>
          <span>0</span>
        </div>
        <div className="relative min-w-0 flex-1 border-b border-gray-200">
          <div className="absolute inset-x-0 top-0 border-t border-gray-100" aria-hidden />
          <div className="absolute inset-x-0 top-1/2 border-t border-gray-100" aria-hidden />
          {point && (
            <div
              role="tooltip"
              className={`pointer-events-none absolute -top-2 z-10 -translate-y-full whitespace-nowrap rounded-lg bg-codialis-navy px-2.5 py-1.5 text-xs text-white shadow-lg ${
                position < 25 ? '' : position > 75 ? '-translate-x-full' : '-translate-x-1/2'
              }`}
              style={{ left: `${position}%` }}
            >
              <div className="font-semibold">{pluriel(point.vues, 'vue')}</div>
              <div className="text-white/80">{point.libelle}</div>
            </div>
          )}
          <ol className="relative flex h-full items-end gap-[2px]" aria-hidden>
            {points.map((un, rang) => (
              <li
                key={un.du}
                className="flex h-full min-w-0 flex-1 cursor-default items-end justify-center"
                onPointerEnter={() => setActif(rang)}
                onPointerLeave={() => setActif(null)}
                onClick={() => setActif(rang)}
              >
                <span
                  className={`w-full max-w-[24px] rounded-t ${actif === rang ? 'bg-codialis-blue' : 'bg-codialis-sky'}`}
                  style={{ height: un.vues === 0 || max === 0 ? 0 : `${Math.max(2, (un.vues / max) * 100)}%` }}
                />
              </li>
            ))}
          </ol>
        </div>
      </div>
      <ol className="ml-8 mt-1.5 flex h-4 gap-[2px]" aria-hidden>
        {points.map((un, rang) => (
          <li key={un.du} className="relative min-w-0 flex-1">
            {rang % intervalle === 0 && (
              <span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-codialis-gray">
                {un.court}
              </span>
            )}
          </li>
        ))}
      </ol>
      <table className="sr-only">
        <caption>Vues des leçons, point par point</caption>
        <thead>
          <tr>
            <th scope="col">Période</th>
            <th scope="col">Vues</th>
          </tr>
        </thead>
        <tbody>
          {points.map((un) => (
            <tr key={un.du}>
              <td>{un.libelle}</td>
              <td>{un.vues}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AucuneVue({ vues, direction }: { vues: Statistiques['vues']; direction: boolean }): JSX.Element {
  if (vues.coursPublies > 0) return <p className="text-sm text-codialis-gray">Aucune leçon ouverte par un apprenant sur la période.</p>;
  if (direction) return <p className="text-sm text-codialis-gray">Ce formateur n’a pas de cours publié.</p>;
  return (
    <p className="text-sm text-codialis-gray">
      Vous n’avez pas de cours publié : les vues de vos leçons s’afficheront ici.{' '}
      <Link to="/instructor-courses" className="font-semibold text-codialis-sky hover:underline">
        Mes cours
      </Link>
    </p>
  );
}

function CarteVues({ statistiques, periode, direction }: { statistiques: Statistiques; periode: string; direction: boolean }): JSX.Element {
  const { vues } = statistiques;
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display mb-1 text-lg font-bold text-codialis-navy">Vues des leçons · {periode}</h2>
      <p className="mb-4 text-xs text-codialis-gray">
        {LIBELLES_PAS[vues.pas]} · un apprenant qui ouvre une leçon dans la journée compte une vue
      </p>
      {vues.total === 0 ? <AucuneVue vues={vues} direction={direction} /> : <Histogramme points={vues.serie} />}
    </section>
  );
}

function LigneModule({ module }: { module: ModuleVu }): JSX.Element {
  return (
    <li className="rounded-xl border border-gray-100 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-codialis-navy">{module.titre}</div>
          <div className="text-[11px] text-codialis-gray">{module.cours.titre}</div>
        </div>
        <span className="shrink-0 text-xs text-codialis-gray">
          {pluriel(module.vues, 'vue')} · {pluriel(module.apprenants, 'apprenant')}
        </span>
      </div>
      <div className="progress-track mt-2 h-1.5 overflow-hidden rounded-full">
        <div className="progress-bar h-full rounded-full" style={{ width: `${module.part}%` }} />
      </div>
    </li>
  );
}

function CarteModules({ statistiques, direction }: { statistiques: Statistiques; direction: boolean }): JSX.Element {
  const { vues } = statistiques;
  return (
    <section className="min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display mb-4 text-lg font-bold text-codialis-navy">Modules les plus vus</h2>
      {vues.modules.length === 0 ? (
        <AucuneVue vues={vues} direction={direction} />
      ) : (
        <ol className="space-y-3">
          {vues.modules.map((module) => (
            <LigneModule key={module.id} module={module} />
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Retours des apprenants
// ---------------------------------------------------------------------------

function Etoiles({ valeur }: { valeur: number | null }): JSX.Element {
  const pleines = valeur === null ? 0 : Math.round(valeur);
  return (
    <span className="flex shrink-0 gap-0.5" aria-hidden>
      {[1, 2, 3, 4, 5].map((rang) => (
        <Star
          key={rang}
          className={`h-4 w-4 ${rang <= pleines ? 'fill-amber-400 text-amber-400' : 'fill-gray-200 text-gray-200'}`}
        />
      ))}
    </span>
  );
}

function Critere({ critere }: { critere: CritereSatisfaction }): JSX.Element {
  return (
    <li className="min-w-0 rounded-xl bg-gray-50 p-3">
      <div className="text-sm font-medium text-codialis-navy">{critere.libelle}</div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Etoiles valeur={critere.moyenne} />
        <span className="text-sm font-bold text-codialis-navy">{surCinq(critere.moyenne)}</span>
        <span className="text-[11px] text-codialis-gray">{pluriel(critere.notes, 'note')}</span>
      </div>
    </li>
  );
}

function bordure(commentaire: CommentaireSatisfaction): string {
  if (commentaire.moyenne === null) return 'border-codialis-sky';
  if (commentaire.moyenne >= 4) return 'border-codialis-mint';
  if (commentaire.moyenne >= 3) return 'border-codialis-sky';
  return 'border-amber-400';
}

function Commentaire({ commentaire }: { commentaire: CommentaireSatisfaction }): JSX.Element {
  return (
    <li className={`rounded-xl border-l-4 bg-gray-50 p-3 ${bordure(commentaire)}`}>
      <p className="whitespace-pre-line break-words text-sm text-codialis-ink">{commentaire.texte}</p>
      <p className="mt-1.5 text-[11px] text-codialis-gray">
        {[
          commentaire.libelleType,
          `le ${jourFrancais(commentaire.repondLe)}`,
          commentaire.moyenne === null ? null : `moyenne ${surCinq(commentaire.moyenne)}`,
          commentaire.formation,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </li>
  );
}

function CarteRetours({ statistiques }: { statistiques: Statistiques }): JSX.Element {
  const { satisfaction } = statistiques;
  const entete = [
    pluriel(satisfaction.reponses, 'réponse'),
    satisfaction.nps.score === null ? null : `NPS ${texteNps(satisfaction.nps.score)}`,
    satisfaction.tauxReponse === null ? null : `${satisfaction.tauxReponse} % de réponse aux envois de la période`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-codialis-navy">Retours apprenants</h2>
          <p className="text-sm text-codialis-gray">{entete}</p>
          <p className="mt-0.5 text-[11px] text-codialis-gray">
            {satisfaction.message} Questionnaires de satisfaction à 2 mois, 6 mois et en fin de parcours ; commentaires
            sans le nom de leur auteur.
          </p>
        </div>
        <Link
          to="/satisfaction"
          className="inline-flex min-h-[36px] shrink-0 items-center text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
        >
          Tout voir dans Satisfaction
        </Link>
      </div>

      {satisfaction.reponses === 0 ? (
        <p className="flex items-center gap-2 text-sm text-codialis-gray">
          <MessageSquareText className="h-4 w-4 shrink-0" aria-hidden />
          Aucune réponse à un questionnaire de satisfaction sur la période.
        </p>
      ) : (
        <>
          <ul className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {satisfaction.criteres.map((critere) => (
              <Critere key={critere.id} critere={critere} />
            ))}
          </ul>
          {satisfaction.commentaires.length === 0 ? (
            <p className="text-sm text-codialis-gray">Aucun commentaire laissé sur la période.</p>
          ) : (
            <>
              <h3 className="mb-2 text-sm font-semibold text-codialis-navy">
                Derniers commentaires
                {satisfaction.commentairesEnTout > satisfaction.commentaires.length && (
                  <span className="font-normal text-codialis-gray">
                    {' '}
                    · {satisfaction.commentaires.length} sur {satisfaction.commentairesEnTout}
                  </span>
                )}
              </h3>
              <ul className="space-y-3">
                {satisfaction.commentaires.map((commentaire) => (
                  <Commentaire key={commentaire.id} commentaire={commentaire} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

function Lecture({ donnees, actualisation }: { donnees: StatistiquesFormateur; actualisation: boolean }): JSX.Element {
  const { periode, comparaison } = donnees;
  return (
    <p className="text-xs text-codialis-gray" aria-live="polite">
      <span className="font-semibold text-codialis-navy">Période : {periode.libelle}</span>
      {periode.arreteeAu < periode.au ? `, arrêtée au ${jourFrancais(periode.arreteeAu)}` : ''}
      {' · '}période précédente : {comparaison.libelle}
      {' · '}
      {actualisation ? 'Actualisation…' : `Mis à jour à ${heureDeMiseAJour(donnees.calculeLe)}`}
    </p>
  );
}

export function InstructorStatsPage(): JSX.Element {
  const [filtre, setFiltre] = useState<FiltreStatistiques>(SANS_FILTRE);
  const { data, isLoading, isFetching, error } = useStatistiquesFormateur(filtre);
  const direction = data?.vue === 'DIRECTION';

  const choisirPeriode = (periode: ChoixPeriode): void =>
    setFiltre((avant) => ({
      ...avant,
      periode,
      // Des dates libres partent de la période affichée.
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.arreteeAu ?? '') : avant.au,
    }));

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="min-w-0">
          <h1 className="font-display text-3xl font-extrabold text-codialis-navy">
            {direction ? 'Statistiques des formateurs' : 'Statistiques de mes cours'}
          </h1>
          <p className="mt-1 text-sm text-codialis-gray">
            {direction && data?.formateur
              ? `Heures animées, vues des leçons et retours des apprenants de ${data.formateur.nom}`
              : 'Vision détaillée de votre impact pédagogique'}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 lg:w-auto lg:justify-end">
          {data?.options.formateurs && data.options.formateurs.length > 0 && (
            <ChampFiltre
              valeur={filtre.formateur || data.formateur?.id || ''}
              onChange={(formateur) => setFiltre((avant) => ({ ...avant, formateur }))}
              etiquette="Formateur"
            >
              {data.options.formateurs.map((formateur) => (
                <option key={formateur.id} value={formateur.id}>
                  {formateur.nom}
                  {formateur.sites.length > 0 ? ` · ${formateur.sites.join(', ')}` : ''}
                </option>
              ))}
            </ChampFiltre>
          )}
          <ChampFiltre valeur={filtre.periode} onChange={(valeur) => choisirPeriode(valeur as ChoixPeriode)} etiquette="Période">
            {(data?.options.periodes ?? [{ valeur: 'MOIS', libelle: 'Mois en cours' }]).map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          {filtre.periode === 'LIBRE' && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <ChampDate
                libelle="Du"
                valeur={filtre.du}
                max={filtre.au || undefined}
                onChange={(du) => setFiltre((avant) => ({ ...avant, du }))}
              />
              <ChampDate
                libelle="au"
                valeur={filtre.au}
                min={filtre.du || undefined}
                onChange={(au) => setFiltre((avant) => ({ ...avant, au }))}
              />
            </div>
          )}
        </div>
      </div>

      {data && <Lecture donnees={data} actualisation={isFetching} />}
      {filtre.periode === 'LIBRE' && filtre.du && filtre.au && filtre.du > filtre.au && (
        <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
      )}
      {isLoading && <Chargement libelle="Chargement des statistiques…" />}
      {!isLoading && error && (
        <MessageErreur message={error instanceof ErreurApi ? error.message : 'Impossible de charger les statistiques.'} />
      )}
      {data?.refus && <p className="rounded-2xl bg-white p-4 text-sm text-codialis-gray shadow-card sm:p-6">{data.refus}</p>}

      {data?.statistiques && (
        <div className={`space-y-6 transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
          <Indicateurs statistiques={data.statistiques} direction={direction} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <CarteVues statistiques={data.statistiques} periode={data.periode.libelle} direction={direction} />
            <CarteModules statistiques={data.statistiques} direction={direction} />
          </div>
          <CarteRetours statistiques={data.statistiques} />
        </div>
      )}
    </div>
  );
}
